/* ============================================================
   CANONICAL DATA SNAPSHOT v8 — anonymized stub (International Demo Company)
   ============================================================
   IMPORTANT — this is a DELIBERATELY ANONYMIZED placeholder.

   Earlier versions of this file embedded real per-operator private
   financial data (cash/card balances, support AP, term debt, recurring
   subscriptions) that shipped to the public lens.cleic.com bundle.
   That violated E-020 (Sacred IP — visibility covenant) by exposing
   PRIVATE-tier data via a PUBLIC-tier surface — anyone curling the
   bundle could read the values.

   This v8 stub satisfies every export the rest of Lens imports, but
   carries only neutral demo values themed on a fictional international
   company ("International Demo Company"). Numbers are realistic-but-modest
   placeholder amounts; all entity names are generic ("Demo Bank A",
   "Demo Vendor 1", etc.).

   The proper fix — multi-Book demo companies driven by Cleic Supabase
   API per F-022 ledger contract — lives behind the Lens auth gate +
   demo-companies refactor (queued in
   memory/project_tomorrow_panel_review_and_dom_alignment.md).

   Until then, this stub renders an empty-ish Lens for everyone, including
   the operator. Real data flows in via /api/ledger.json once the
   demo-companies refactor lands.
   ============================================================ */

export const SNAPSHOT_AS_OF = '2026-04-19';
export const DASHBOARD_VERSION = 'v8.0.0-stub';

/* CC processing fee convention placeholder — generic, no operator-specific. */
export const CC_FEE_RATE = 0.01;

/* Demo FX convention placeholder — not a provider/live rate. */
export const FX_USD_PHP_BASE = 61.0;

/* ============================================================
   BALANCES — anonymized stub. Real values come from API per Book.
   ============================================================ */
export const BALANCES = {
  bank: [
    { name: 'Demo Bank A',          amount: 25000.00, dust: false, note: 'Primary cash hub (demo)' },
    { name: 'Demo Bank B Savings',  amount:  5000.00, dust: false },
    { name: 'Demo Bank C',          amount:   500.00, dust: true,  note: 'Maintaining balance (demo)' },
  ],
  cash: [
    { name: 'Demo E-Wallet',        amount:  3000.00, dust: false, note: 'Digital wallet (demo)' },
    { name: 'Demo Wallet',          amount:  1500.00, dust: false, note: 'Physical cash (demo)' },
  ],
  creditCards: [
    { name: 'Demo Card 1',          amount: -15000.00, role: 'shared',  note: 'Primary expense card (demo)' },
    { name: 'Demo Card 2',          amount:  -5000.00, role: 'fx',      note: 'FX subscriptions (demo)' },
    { name: 'Demo Card 3',          amount:    500.00, role: 'credit' },
  ],
  suspense: [
    { name: 'Demo Suspense Outflow', amount: -1000.00, note: 'Demo unreconciled' },
  ],
  longTermLiabilities: [
    { name: 'Demo Property A Loan',  amount: -750000.00 },
    { name: 'Demo Accounts Payable', amount:  -10000.00 },
    { name: 'Demo Tax Payable',      amount:    -500.00 },
  ],
  equity: [
    { name: 'Demo Retained Earnings', amount: 100000.00 },
    { name: 'Demo Equity Adjustments', amount:  10000.00 },
  ],
};

/* ============================================================
   SALARY — anonymized stub.
   ============================================================ */
export const SALARY = {
  employer: 'Demo Employer LLC',
  rateHourly: 500.00,
  hoursPerDay: 8,
  workingDaysPerFortnight: 11,
  payAccount: 'Demo Bank A',
  scheduleNote: 'Demo fortnightly pay schedule.',
  withholdingTaxPerPay: { min: 3000, max: 3500, mid: 3250 },
  govContributions: [
    { name: 'Demo Gov Contribution 1', amount: 1500, bankFee: 5 },
    { name: 'Demo Gov Contribution 2', amount:  500, bankFee: 5 },
    { name: 'Demo Gov Contribution 3', amount:  500, bankFee: 15 },
    { name: 'Demo Gov Contribution 4', amount:  750, bankFee: 23 },
  ],
};

/* ============================================================
   RECURRING — anonymized stub.
   ============================================================ */
export const RECURRING = {
  monthly: [
    { code: 'MM01', vendor: 'Demo Subscription 1',  amount:   200.00, cat: 'Software',      account: 'Demo Card 1', paymentSchedule: null },
    { code: 'MM02', vendor: 'Demo Subscription 2',  amount:   500.00, cat: 'Software',      account: 'Demo Card 1', paymentSchedule: null },
    { code: 'MM03', vendor: 'Demo Subscription 3',  amount:   150.00, cat: 'Entertainment', account: 'Demo E-Wallet', paymentSchedule: null },
    { code: 'MM04', vendor: 'Demo Bank Fee',        amount:   100.00, cat: 'Bank Fees',     account: 'Demo Card 1', paymentSchedule: null },
    { code: 'MM05', vendor: 'Demo Health Subscription', amount: 1500.00, cat: 'Health',     account: 'Demo E-Wallet', paymentSchedule: null },
  ],
  yearly: [
    { code: 'YY01', vendor: 'Demo Annual 1',        amount: 1000.00, month:  3, cat: 'Membership',  paymentSchedule: null },
    { code: 'YY02', vendor: 'Demo Annual 2',        amount: 2500.00, month:  6, cat: 'Software',    paymentSchedule: null },
  ],
  quarterly: [
    { code: 'QQ01', vendor: 'Demo Quarterly Savings', amount: 3000.00, cat: 'Savings', account: 'Demo Bank B Savings', paymentSchedule: null },
  ],
};

/* ============================================================
   SUPPORT AP — anonymized stub.
   ============================================================ */
export const FAMILY_AP = {
  direct: {
    vendor: 'Demo Direct Support',
    contactId: 'demo-contact-1',
    monthlyAmount: 10000,
    previousAmount: 8000,
    bumpedYM: '2025-08',
    bumpNote: 'Demo bump',
    outstandingPayable: 0,
    unusedCredits: 0,
    channels: [
      { name: 'Demo Channel A', estMonthly: 4000, cat: 'Demo Category 1' },
      { name: 'Demo Channel B', estMonthly: 3000, cat: 'Demo Category 2' },
      { name: 'Demo Channel C', estMonthly: 3000, cat: 'Demo Category 3' },
    ],
    paymentSchedule: null,
    note: 'Demo direct support obligation',
  },
  viaMarian: {
    vendor: 'Demo Routed Vendor',
    contactId: 'demo-contact-2',
    monthlyAmount: 5000,
    previousAmount: 4500,
    bumpedYM: '2026-04',
    billDayOfMonth: 1,
    terms: 'Net 15',
    dueDayOfMonth: 16,
    account: 'Demo Bank A',
    arrears: 0,
    arrearsAsOf: '2026-04-19',
    cat: 'Support',
    paymentSchedule: null,
    openQuestion: null,
    note: 'Demo routed support',
    paymentHistory: [],
  },
};

/* ============================================================
   AR — anonymized stub.
   ============================================================ */
export const AR_RECURRING = {
  educationFund: [
    { from: 'Demo Contributor 1', contactId: 'demo-contact-3', amount: 1000, billDay: 1, collectDay: 5, account: 'Demo Bank A', contribType: 'contributor', collectionRate: 1.00, note: 'Demo' },
    { from: 'Demo Contributor 2', contactId: 'demo-contact-4', amount: 1500, billDay: 1, collectDay: 5, account: 'Demo Bank A', contribType: 'contributor', collectionRate: 0.60, note: 'Demo' },
    { from: 'Demo Contributor 3', contactId: 'demo-contact-5', amount: 2000, billDay: 1, collectDay: 5, account: 'Demo Bank A', contribType: 'contributor', collectionRate: 0.50, note: 'Demo' },
  ],
  familyAllowance: [
    { from: 'Demo Contributor 1', contactId: 'demo-contact-3', amount: 5000, billDay: 1, collectDay: 10, account: 'Demo Bank A', contribType: 'pooled-contribution', collectionRate: 1.00, note: 'Demo' },
  ],
  avgCollectionDays: 7,
};

/* ============================================================
   MORTGAGES — anonymized stub.
   ============================================================ */
export const MORTGAGES = [
  { name: 'Demo Property A', monthlyPayment: 5000.00, zbBillAmount: 5000.00, bankFee: 5, outstanding: 750000.00, estRate: 0.075, vendor: 'Demo Lender 1', account: 'Demo Bank A', note: 'Demo property loan' },
];

/* ============================================================
   INCOMES — anonymized stub.
   ============================================================ */
export const INCOMES = {
  jmShortLoan: {
    name: 'Demo Short-term Loan',
    amount: 0,
    expectedDate: null,
    defaultRepaySchedule: [],
    note: 'Demo placeholder',
  },
  jmPayDirect: {
    code: 'DEMO_PAY_DIRECT',
    name: 'Demo Custody Stream',
    frequency: 'monthly',
    direction: 'outbound',
    note: 'Demo placeholder',
    defaultSchedule: {
      startYM: '2026-05',
      stopYM: null,
      steps: [{ fromYM: '2026-05', amount: 0, note: 'Demo zero baseline' }],
    },
  },
  fvfRevenue: {
    name: 'Demo Recurring Revenue',
    launchYM_options: ['2026-07', '2026-08', '2026-09', null],
    launchYM_default: '2026-08',
    targetMRR_options: [0, 5000, 10000, 20000, 50000],
    targetMRR_default: 10000,
    rampMonths: 3,
    note: 'Demo recurring stream',
  },
};

/* ============================================================
   CC MIN PAYMENTS — anonymized stub.
   ============================================================ */
export const CC_MIN_PAYMENTS = [
  { card: 'Demo Card 1', estMin: 1000, interestRate: 0.0333 },
  { card: 'Demo Card 2', estMin:  500, interestRate: 0.0350 },
];

/* ============================================================
   PRESETS — scenario knobs (no PII). Shape preserved.
   ============================================================ */
const SCENARIO_BASE = {
  mtbContinuity: true,
  jmLoanReceived: false,
  ccPaydownPctOfFreeCash: 0.50,
  fvfLaunchYM: '2026-08',
  fvfTargetMRR: 10000,
  payDirectEnabled: false,
  familyAllowanceMode: 'current',
  jpMode: 'current',
  jpAmount: 2000,
  jcAmount: 1500,
  jmAmount: 1000,
  tableOrientation: 'horizontal',
  statementStyle: 'analytical',
  comparisonMode: 'none',
  cpiAnnualPct: 0.04,
  foodCpiAnnualPct: 0.05,
  familyPressurePct: 0.03,
  seasonalEnabled: true,
  seasonalMultiplier: 1.00,
  lifestyleEnabled: true,
  lifestyleMultiplier: 1.00,
  fuelEnabled: true,
  fuelPricePerLiter: 72,
  fuelMonthlyLiters: 40,
  fuelShockPct: 0.00,
  fvfMethod: 'triangulated',
  fvfTopDown_marketShare: 0.00015,
  fvfTopDown_arpu: 500,
  fvfBottomUp_initialTenants: 3,
  fvfBottomUp_newPerMonth: 2,
  fvfBottomUp_churn: 0.05,
  fvfBottomUp_arpu: 500,
};

export const PRESETS = {
  base:     { name: 'Base',     ...SCENARIO_BASE },
  upside:   { name: 'Upside',   ...SCENARIO_BASE, ccPaydownPctOfFreeCash: 0.75, fvfTargetMRR: 20000, cpiAnnualPct: 0.03, seasonalMultiplier: 1.10, lifestyleMultiplier: 1.20 },
  downside: { name: 'Downside', ...SCENARIO_BASE, mtbContinuity: false, ccPaydownPctOfFreeCash: 0.25, fvfTargetMRR: 5000, cpiAnnualPct: 0.06, seasonalMultiplier: 0.80, lifestyleEnabled: false, fuelShockPct: 0.30 },
};

/* ============================================================
   Demo book defaults + chart-of-accounts mapping (no PII).
   ============================================================ */
export const FVF_BOOK_DEFAULTS = {
  currency: 'USD',
  defaultCurrency: 'USD',
  reportingCurrency: 'USD',
  accountingStandard: 'IFRS',
  country: 'GLOBAL',
  timezone: 'UTC',
  dateFormat: 'YYYY-MM-DD',
  numberFormat: '1,000.00',
};

export const FVF_ACCOUNT_MAP = [
  { key: 'bank',                type: 'Bank',                fsCat: 'asset',     cat: 'Real (BS)', sub: 'Cash and Cash Equivalents', cfCat: 'Operating', cfSub: 'Operating-Cash',             current: true,  parent: '1000' },
  { key: 'cash',                type: 'Cash',                fsCat: 'asset',     cat: 'Real (BS)', sub: 'Cash and Cash Equivalents', cfCat: 'Operating', cfSub: 'Operating-Cash',             current: true,  parent: '1000' },
  { key: 'accountsReceivable',  type: 'Accounts Receivable', fsCat: 'asset',     cat: 'Real (BS)', sub: 'Accounts Receivable',       cfCat: 'Operating', cfSub: 'Operating-WC',               current: true,  parent: '1100' },
  { key: 'creditCards',         type: 'Credit Card',         fsCat: 'liability', cat: 'Real (BS)', sub: 'Credit Card Payable',       cfCat: 'Financing', cfSub: 'Financing-ShortTermDebt',    current: true,  parent: '2100' },
  { key: 'accountsPayable',     type: 'Accounts Payable',    fsCat: 'liability', cat: 'Real (BS)', sub: 'Accounts Payable',          cfCat: 'Operating', cfSub: 'Operating-WC',               current: true,  parent: '2000' },
  { key: 'longTermLiabilities', type: 'Long-Term Liability', fsCat: 'liability', cat: 'Real (BS)', sub: 'Mortgages Payable',         cfCat: 'Financing', cfSub: 'Financing-LongTermDebt',     current: false, parent: '2500' },
  { key: 'equity',              type: 'Equity',              fsCat: 'equity',    cat: 'Real (BS)', sub: 'Retained Earnings',         cfCat: null,        cfSub: null,                         current: false, parent: '3000' },
  { key: 'salaryRevenue',       type: 'Revenue',             fsCat: 'revenue',   cat: 'Nominal (IS)', sub: 'Primary Income',         cfCat: 'Operating', cfSub: 'Operating-Inflow',           current: null,  parent: '4000' },
  { key: 'fvfRevenue',          type: 'Revenue',             fsCat: 'revenue',   cat: 'Nominal (IS)', sub: 'Recurring Revenue',      cfCat: 'Operating', cfSub: 'Operating-Inflow',           current: null,  parent: '4000' },
  { key: 'familyContribRevenue',type: 'Other Income',        fsCat: 'revenue',   cat: 'Nominal (IS)', sub: 'Other Contributions',    cfCat: 'Operating', cfSub: 'Operating-Inflow',           current: null,  parent: '4900' },
  { key: 'subscriptionsExpense',type: 'Expense',             fsCat: 'expense',   cat: 'Nominal (IS)', sub: 'Software & Subscriptions', cfCat: 'Operating', cfSub: 'Operating-Outflow',        current: null,  parent: '5000' },
  { key: 'familyExpense',       type: 'Expense',             fsCat: 'expense',   cat: 'Nominal (IS)', sub: 'Operating Support',      cfCat: 'Operating', cfSub: 'Operating-Outflow',          current: null,  parent: '5100' },
  { key: 'educationExpense',    type: 'Expense',             fsCat: 'expense',   cat: 'Nominal (IS)', sub: 'Education',              cfCat: 'Operating', cfSub: 'Operating-Outflow',          current: null,  parent: '5200' },
  { key: 'mortgageInterest',    type: 'Expense',             fsCat: 'expense',   cat: 'Nominal (IS)', sub: 'Interest Expense',       cfCat: 'Financing', cfSub: 'Financing-InterestPayment',  current: null,  parent: '6000' },
  { key: 'taxesAndGov',         type: 'Expense',             fsCat: 'expense',   cat: 'Nominal (IS)', sub: 'Taxes & Statutory',      cfCat: 'Operating', cfSub: 'Operating-Outflow',          current: null,  parent: '5900' },
];

/* Category → hex color (Recharts). No PII. */
export const CAT_COLORS = {
  Software:       '#60a5fa',
  Entertainment:  '#c084fc',
  'Bank Fees':    '#f0b429',
  Education:      '#4ade80',
  Membership:     '#fb923c',
  Health:         '#f87171',
  Savings:        '#22d3ee',
  Support:        '#f472b6',
  Housing:        '#fbbf24',
  Income:         '#4ade80',
  Deductions:     '#f87171',
  Routed:         '#e879f9',
  Seasonal:       '#fcd34d',
  Lifestyle:      '#a78bfa',
  Fuel:           '#fb7185',
  Gifts:          '#f9a8d4',
  Donations:      '#fde68a',
};

/* ============================================================
   SEASONAL — anonymized stub.
   ============================================================ */
export const SEASONAL = {
  events: [
    { id: 'demo-celebration-1', label: 'Demo Celebration 1',  month:  3, amount: 2000, type: 'celebration', note: 'Generic seasonal celebration (demo)' },
    { id: 'demo-celebration-2', label: 'Demo Celebration 2',  month:  6, amount: 2000, type: 'celebration', note: 'Generic seasonal celebration (demo)' },
    { id: 'demo-celebration-3', label: 'Demo Year-End',       month: 12, amount: 5000, type: 'celebration', note: 'Generic year-end celebration (demo)' },
    { id: 'demo-gift-1',        label: 'Demo Gifts',          month: null, amount: 3000, type: 'gift',       note: 'Generic gifting demo' },
    { id: 'demo-donation-1',    label: 'Demo Donations',      month: null, amount: 3000, type: 'donation',   note: 'Generic donation demo' },
  ],
  categoriesEnabled: { celebration: true, gift: true, donation: true, fiesta: true },
};

export const LIFESTYLE = {
  events: [
    { id: 'demo-lifestyle-1', label: 'Demo Lifestyle 1', month: null, amount: 3000, type: 'gear',   note: 'Generic lifestyle demo' },
    { id: 'demo-lifestyle-2', label: 'Demo Lifestyle 2', month: null, amount: 2000, type: 'health', note: 'Generic lifestyle demo' },
  ],
  categoriesEnabled: { race: true, gear: true, health: true },
};

/* ============================================================
   INFLATION + FUEL — generic macro (no PII).
   ============================================================ */
export const INFLATION = {
  phCpiAnnualPct:        0.04,
  phCpiAnnualPct_options:[0.02, 0.03, 0.04, 0.05, 0.06, 0.08, 0.10],
  foodCpiAnnualPct:      0.05,
  foodCpiAnnualPct_options:[0.03, 0.04, 0.05, 0.06, 0.08, 0.10],
  familyAllowancePressurePct: 0.03,
  familyAllowancePressurePct_options:[0, 0.02, 0.03, 0.05, 0.08, 0.10],
  note: 'CPI compounds monthly; support pressure = (baseSupport x (1+cpi)^years) x (1+pressure)^years',
};

export const FUEL = {
  phPumpPricePerLiter:     72,
  phPumpPrice_options:     [55, 60, 65, 70, 72, 80, 90, 110],
  monthlyLitersConsumed:   40,
  monthlyLiters_options:   [0, 20, 30, 40, 50, 60, 80],
  geopoliticalShockPct:    0.00,
  geopoliticalShock_options:[-0.20, -0.10, 0.00, 0.10, 0.20, 0.30, 0.50],
  note: 'Generic energy-cost macro demo.',
};

/* ============================================================
   Universal recurring-model drivers — generic forecasting (no PII).
   ============================================================ */
export const FVF_DRIVERS = {
  methods: ['top-down', 'bottom-up', 'triangulated'],
  defaultMethod: 'triangulated',
  topDown: {
    label: 'Top-down (addressable units x reach x revenue/unit)',
    tamTenants: 670000, tamTenants_options: [100000, 300000, 670000, 1000000],
    marketSharePctY1: 0.00015, marketShare_options: [0.00003, 0.0001, 0.00015, 0.0003, 0.001],
    arpuPerMonth: 500, arpuPerMonth_options: [200, 300, 500, 900, 1500],
    note: 'Addressable units x reach x average recurring revenue per unit. Strategic ceiling lens.',
  },
  bottomUp: {
    label: 'Bottom-up (active units x revenue/unit)',
    initialTenants: 3, initialTenants_options: [1, 3, 5, 10, 20],
    monthlyNewTenants: 2, monthlyNewTenants_options: [0, 1, 2, 5, 10, 20],
    churnRateMonthly: 0.05, churnRate_options: [0, 0.02, 0.05, 0.08, 0.10, 0.15],
    arpuPerMonth: 500, arpuPerMonth_options: [200, 300, 500, 900, 1500],
    note: 'Grounded truth: active unit base + net adds + attrition. Most realistic for early stage.',
  },
  triangulated: {
    label: 'Triangulated (70% bottom-up, 20% top-down, 10% target recurring revenue)',
    weights: { bottomUp: 0.70, topDown: 0.20, scenarioMRR: 0.10 },
    note: 'Hybrid: bottom-up anchors operating reality, top-down stretches strategic ambition.',
  },
};

/* ============================================================
   GLOSSARY — definitions only (general). Generic / non-PII.
   ============================================================ */
export const GLOSSARY = {
  DSO:     { term: 'Days Sales Outstanding', short: 'Average days to collect cash after billing.',  long: 'DSO = (AR / Credit Sales) × Days. Lower is better.', formula: '(AR / Credit Sales) × Period Days' },
  DPO:     { term: 'Days Payable Outstanding', short: 'Average days to pay vendors after billing.',  long: 'DPO = (AP / COGS) × Days. Higher = more float.',     formula: '(AP / COGS) × Period Days' },
  CCC:     { term: 'Cash Conversion Cycle', short: 'Days between cash out and cash in.',             long: 'CCC = DSO − DPO. Negative = paid before paying.',     formula: 'DSO − DPO' },
  FCF:     { term: 'Free Cash Flow',    short: 'Cash left after operating outflows.',                long: 'FCF = Gross In − Burn.',                              formula: 'Operating In − Operating Out' },
  Runway:  { term: 'Cash Runway',       short: 'Months liquid reserves last at current burn.',       long: 'Runway = Liquid / |Net Burn|.',                       formula: 'Liquid / |Burn|' },
  DCF:     { term: 'Discounted Cash Flow', short: 'PV of future cash flows.',                        long: 'Σ(FCF_t / (1+r)^t) + TV/(1+r)^n.',                    formula: 'Σ FCF/(1+r)^t + TV' },
  MRR:     { term: 'Monthly Recurring Revenue', short: 'Predictable monthly recurring revenue.',      long: 'Active units/accounts/customers x average recurring revenue per unit.', formula: 'Units x ARPU' },
  ARPU:    { term: 'Average Revenue Per Unit', short: 'Recurring revenue / active unit count.',       long: 'Guides pricing, packaging, volume, or account economics.',              formula: 'MRR / Units' },
  Churn:   { term: 'Monthly Churn Rate', short: 'Fraction of customers cancelling monthly.',          long: '5% monthly = 46% annual retention.',                  formula: 'Cancelled / Starting' },
  'Top-down':     { term: 'Top-down forecasting',    short: 'Start from addressable market x reach x revenue/unit.', long: 'TAM/SAM/SOM-style market sizing adapted to any model stack.', formula: 'Addressable units x Reach x ARPU' },
  'Bottom-up':    { term: 'Bottom-up forecasting',   short: 'Start from operating unit count and cadence.',          long: 'units_t = units_t-1 + net adds - attrition.',                 formula: '(t-1 + new - churn) x ARPU' },
  Triangulated:   { term: 'Triangulated forecasting',short: 'Weighted blend of top-down, bottom-up, and target.',    long: 'Blend strategic ceiling, operating base, and target run-rate.', formula: '0.7 x BU + 0.2 x TD + 0.1 x Target' },
  CPI:     { term: 'Consumer Price Index', short: 'Annual inflation rate.', long: 'International demo headline assumption. Compounds monthly.',                     formula: 'future = present x (1+cpi)^years' },
  Fuel:    { term: 'Energy unit cost', short: 'Energy cost x monthly usage = burn.', long: 'International demo baseline with shock wedge.',                           formula: 'units x (base x (1+shock))' },
  monthlyBurn:   { term: 'Monthly Burn',          short: 'Total cash outflow per month.',     long: 'Recurring + support + term debt + lifestyle + fuel + seasonal.', formula: 'Σ outflows' },
  monthlyCashIn: { term: 'Monthly Cash In',       short: 'Total cash inflow per month.',      long: 'Net primary income + AR + recurring revenue + lump sums.',        formula: 'Σ inflows' },
  freeCashFlow:  { term: 'Free Cash Flow (FCF)',  short: 'monthlyCashIn − monthlyBurn.',      long: 'Positive compounds liquid; negative eats runway.',              formula: 'cashIn − burn' },
  runwayMonths:  { term: 'Cash Runway (months)',  short: 'Months at current net burn rate.',  long: 'Walks forward until liquid hits zero.',                          formula: 'count(months until liquid ≤ 0)' },
  netWorth:      { term: 'Net Worth (proxy)',     short: 'Liquid assets minus short-term debt minus long-term liabilities.', long: 'Management proxy, not filing basis.', formula: 'liquid - shortTermDebt - ltl' },
  liquidAssets:  { term: 'Liquid Assets',         short: 'Sum of bank + cash + e-wallet within 24h.', long: 'Excludes maintaining-balance and locked savings.',       formula: 'Σ bank.amount + Σ cash.amount' },
  ccDebt:        { term: 'Short-term Card Debt',  short: 'Sum of card or revolving balances with negative amount.', long: 'Positive balances are tracked separately.', formula: '|sum revolving balances where amount < 0|' },
  ccCredits:     { term: 'Card Credits',          short: 'Sum of card or revolving balances currently positive.', long: 'Statement credits / overpayments.',           formula: 'sum revolving balances where amount > 0' },
  ltl:           { term: 'Long-term Liabilities', short: 'Term debt + AP + tax - long-horizon.',       long: 'Sign-flipped to positive for KPI display.',              formula: '|Σ longTermLiabilities|' },
  equity:        { term: 'Equity (book)',         short: 'Retained earnings + adjustments.',           long: 'What the books say accumulated value is.',                formula: 'Σ equity' },
  suspense:      { term: 'Suspense Balance',      short: 'Unreconciled outflows to chase.',            long: 'Drive toward zero for hygiene.',                          formula: 'Σ suspense' },
  monthlyMortgages:  { term: 'Monthly Term Debt',      short: 'Total monthly term-debt payment.',        long: 'Sum across scheduled term-debt obligations.',           formula: 'Σ termDebt.monthlyPayment' },
  mortgageInterest:  { term: 'Term Debt Interest',     short: 'Interest portion of monthly payment.',    long: 'outstanding x rate / 12.',                              formula: 'Σ (outstanding x rate / 12)' },
  mortgagePrincipal: { term: 'Term Debt Principal',    short: 'Principal portion of payment.',           long: 'monthlyTermDebt - termDebtInterest.',                   formula: 'monthlyTermDebt - termDebtInterest' },
  marianArrears:     { term: 'Partner-Channel Arrears',short: 'Outstanding overdue partner-channel AP.', long: 'Drives DPO drag.',                                      formula: 'partner support arrears' },
  netEducationBurden:{ term: 'Net Support Burden',     short: 'Routed AP minus expected contribution AR.', long: 'Net operating support cost after AR.',                  formula: 'AP - AR' },
  educationAR:       { term: 'Contribution AR (expected)', short: 'Contribution receivables, rate-adjusted.', long: 'scheduled x collectionRate.',                         formula: 'Σ contributors x rate' },
  familyPoolAR:      { term: 'Pool Contribution AR',   short: 'Pool contribution monthly.',              long: 'Demo placeholder.',                                    formula: 'AR_RECURRING.familyAllowance amount' },
  FVFValue:          { term: 'Recurring Revenue DCF',  short: '5-yr DCF + terminal value of recurring revenue.', long: 'Sensitive to discount rate.',                  formula: 'Σ ARR/(1+r)^t + TV' },
  recurringSubs:     { term: 'Monthly Recurring Subscriptions', short: 'Monthly-equivalent of all recurrings.', long: 'Sum of monthly + yearly/12 + quarterly·4/12.', formula: 'Σ monthly + Σ yearly/12 + Σ quarterly·4/12' },
  familyDirect:      { term: 'Direct Operating AP',    short: 'Direct support payable.',                 long: 'Demo placeholder.',                                    formula: 'Σ direct.channels.estMonthly x CPI' },
  marianAP:          { term: 'Partner Channel AP',     short: 'Support routed via partner.',             long: 'Net-15 terms; lumpy realized.',                        formula: 'partner support monthly amount x multipliers' },
  salaryGross:       { term: 'Primary Income (Gross)', short: 'Pre-tax pre-contribution primary income.', long: 'Demo placeholder.',                                    formula: 'Cadence × periods' },
  salaryNet:         { term: 'Primary Income (Net)',   short: 'Take-home after tax + contributions.',     long: 'Gross - withholding tax - statutory contributions.',   formula: 'Gross - tax - statutory' },
  fvfMRR:            { term: 'Recurring Revenue',      short: 'Predictable recurring revenue.',           long: 'Calculated via active driver method.',                  formula: 'recurringRevenue(modelState, ym)' },
  fuel:              { term: 'Monthly Fuel',           short: 'Monthly fuel spend, CPI-adjusted.',       long: 'liters × price × (1 + shock) × CPI.',                  formula: 'liters × price × shock × CPI' },
  seasonal:          { term: 'Seasonal Expense',       short: 'One-off / cyclical expense.',             long: 'Per-event; SeasonalEditor configurable.',              formula: 'Σ events × overrides' },
  lifestyle:         { term: 'Lifestyle Expense',      short: 'Discretionary lifestyle spend.',          long: 'Discretionary = first to cut in downturn.',            formula: 'base × (travel% + lifestyle%)' },
  arTotal:           { term: 'Total Accounts Receivable', short: 'All inflow streams excluding primary income.', long: 'Drives DSO + cash conversion.',                 formula: 'contributionAR + poolAR + recurringRevenue' },
  savingsRate:       { term: 'Savings Rate',           short: 'Net income as % of gross income.',        long: 'Core reserve-formation metric.',                       formula: 'netIncome / grossRevenue' },
  FX:                { term: 'FX-aware line item',     short: 'Subscription billed in foreign currency.', long: 'phpAmount = nativeAmount × resolveFXRate(fx, scenario, ym).', formula: 'native × FX' },
};

/* ============================================================
   CHART OF ACCOUNTS — derived from BALANCES + RECURRING + MORTGAGES.
   Same shape as v7. No PII because BALANCES etc. are anonymized.
   ============================================================ */
const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function classifyBalanceTier(amount, dustFlag) {
  if (dustFlag === true)  return 'dust';
  if (dustFlag === false) return 'primary';
  if (Math.abs(amount) >= 10000) return 'primary';
  if (Math.abs(amount) >= 500)   return 'secondary';
  return 'dust';
}
function classifyMonthlyTier(monthly) {
  if (monthly >= 1000) return 'primary';
  if (monthly >= 200)  return 'secondary';
  return 'dust';
}

export const CHART_OF_ACCOUNTS = [
  ...BALANCES.bank.map((a) => ({ id: `asset.bank.${slug(a.name)}`, label: a.name, type: 'asset', subtype: 'bank', tier: classifyBalanceTier(a.amount, a.dust), balance: a.amount, note: a.note, source: 'BALANCES.bank' })),
  ...BALANCES.cash.map((a) => ({ id: `asset.cash.${slug(a.name)}`, label: a.name, type: 'asset', subtype: 'cash', tier: classifyBalanceTier(a.amount, a.dust), balance: a.amount, note: a.note, source: 'BALANCES.cash' })),
  ...BALANCES.creditCards.map((a) => ({ id: `liability.cc.${slug(a.name)}`, label: a.name, type: a.amount < 0 ? 'liability' : 'asset', subtype: 'creditCard', tier: classifyBalanceTier(a.amount), balance: a.amount, role: a.role, note: a.note, source: 'BALANCES.creditCards' })),
  ...BALANCES.longTermLiabilities.map((a) => ({ id: `liability.lt.${slug(a.name)}`, label: a.name, type: 'liability', subtype: 'longTerm', tier: 'primary', balance: a.amount, note: a.note, source: 'BALANCES.longTermLiabilities' })),
  ...BALANCES.suspense.map((a) => ({ id: `asset.suspense.${slug(a.name)}`, label: a.name, type: 'asset', subtype: 'suspense', tier: classifyBalanceTier(a.amount), balance: a.amount, note: a.note, source: 'BALANCES.suspense' })),
  ...BALANCES.equity.map((a) => ({ id: `equity.${slug(a.name)}`, label: a.name, type: 'equity', subtype: 'retained', tier: 'primary', balance: a.amount, note: a.note, source: 'BALANCES.equity' })),
  ...RECURRING.monthly.map((r) => ({ id: `expense.recurring.monthly.${slug(r.code + '-' + r.vendor)}`, label: `${r.vendor} (${r.code})`, type: 'expense', subtype: 'recurring', tier: classifyMonthlyTier(r.amount), monthly: r.amount, cat: r.cat, account: r.account, note: r.note, fx: !!r.fx, source: 'RECURRING.monthly' })),
  ...RECURRING.yearly.map((r) => ({ id: `expense.recurring.yearly.${slug(r.code + '-' + r.vendor)}`, label: `${r.vendor} (${r.code}, yearly)`, type: 'expense', subtype: 'recurring', tier: classifyMonthlyTier(r.amount / 12), monthly: r.amount / 12, annual: r.amount, cat: r.cat, note: r.note, fx: !!r.fx, source: 'RECURRING.yearly' })),
  ...RECURRING.quarterly.map((r) => ({ id: `expense.recurring.quarterly.${slug(r.code + '-' + r.vendor)}`, label: `${r.vendor} (${r.code}, quarterly)`, type: 'expense', subtype: 'recurring', tier: classifyMonthlyTier(r.amount * 4 / 12), monthly: r.amount * 4 / 12, quarterly: r.amount, cat: r.cat, account: r.account, note: r.note, source: 'RECURRING.quarterly' })),
  ...MORTGAGES.map((m) => ({ id: `expense.mortgage.${slug(m.name)}`, label: `${m.name} mortgage`, type: 'expense', subtype: 'mortgage', tier: 'primary', monthly: m.monthlyPayment, outstanding: m.outstanding, rate: m.estRate, vendor: m.vendor, account: m.account, note: m.note, source: 'MORTGAGES' })),
  { id: 'expense.family.direct', label: 'Demo Support Direct', type: 'expense', subtype: 'family', tier: 'primary', monthly: FAMILY_AP.direct.monthlyAmount, note: FAMILY_AP.direct.note, source: 'SUPPORT_AP.direct' },
  { id: 'expense.support.routed', label: 'Demo Support Routed (Net 15)', type: 'expense', subtype: 'family', tier: 'primary', monthly: FAMILY_AP.viaMarian.monthlyAmount, note: FAMILY_AP.viaMarian.note, source: 'SUPPORT_AP.routed' },
  { id: 'income.salary.primary', label: `Demo primary income - ${SALARY.employer}`, type: 'income', subtype: 'primary-income', tier: 'primary', monthly: SALARY.rateHourly * SALARY.hoursPerDay * SALARY.workingDaysPerFortnight * 2, source: 'PRIMARY_INCOME' },
  ...AR_RECURRING.educationFund.map((s) => ({ id: `income.ar.education.${slug(s.from)}`, label: `${s.from} - contribution AR`, type: 'income', subtype: 'ar', tier: 'primary', monthly: s.amount, rate: s.collectionRate, note: s.note, source: 'AR_RECURRING.educationFund' })),
  ...AR_RECURRING.familyAllowance.map((s) => ({ id: `income.ar.familypool.${slug(s.from)}`, label: `${s.from} - pooled contribution`, type: 'income', subtype: 'ar', tier: 'primary', monthly: s.amount, rate: s.collectionRate, note: s.note, source: 'AR_RECURRING.familyAllowance' })),
  { id: 'income.subscription.mrr', label: 'Demo recurring revenue (forecast)', type: 'income', subtype: 'recurring-revenue', tier: 'primary', monthly: 0, source: 'SUBSCRIPTION_DRIVERS' },
];

export const COA_GROUPS = {
  asset:     'Assets',
  liability: 'Liabilities',
  equity:    'Equity',
  income:    'Income',
  expense:   'Expenses',
};

/* ============================================================
   NC-Assets, long-term liabilities, cash + CC seed snapshots.
   ============================================================ */
export const FIXED_ASSETS = [
  { id: 'asset-property-a', name: 'Demo Property A', category: 'Real Estate', costBasis: 1000000, currentValue: 1000000, accumDepreciation: 0, liabilityLink: 'demo-mortgage-a', purchaseDate: '2021-01-01', status: 'held', note: 'Demo' },
  { id: 'asset-vehicle',    name: 'Demo Vehicle',    category: 'Vehicle',     costBasis: 0, currentValue: 0, accumDepreciation: 0, liabilityLink: null, purchaseDate: null, status: 'not-yet-acquired', note: 'Demo placeholder' },
];

export const LONG_TERM_LIABILITIES = [
  { id: 'demo-mortgage-a', name: 'Demo Mortgage A', outstanding: 750000, monthlyPayment: 5000, interestRate: 0.075, amortizeMonths: 240, lastPaymentYM: '2026-04', linkedAsset: 'asset-property-a', note: 'Demo' },
];

export const CASH_BALANCES = [
  { id: 'demo-bank-a',    name: 'Demo Bank A',         type: 'bank', balance: 25000.00, liquid: true },
  { id: 'demo-bank-b',    name: 'Demo Bank B Savings', type: 'bank', balance:  5000.00, liquid: true },
  { id: 'demo-ewallet',   name: 'Demo E-Wallet',       type: 'cash', balance:  3000.00, liquid: true },
  { id: 'demo-wallet',    name: 'Demo Wallet',         type: 'cash', balance:  1500.00, liquid: true },
];

export const CC_LIABILITIES = [
  { id: 'demo-cc-1', card: 'Demo Card 1', balance: 15000, limit: 50000, minPct: 0.0333, note: 'Demo primary' },
  { id: 'demo-cc-2', card: 'Demo Card 2', balance:  5000, limit: 30000, minPct: 0.0350, note: 'Demo FX' },
];
